import type { AutomergeUrl } from "@automerge/automerge-repo";
import { useDocument } from "@automerge/react";
import {
  createFileRoute,
  Outlet,
  redirect,
  useMatchRoute,
  useParams,
} from "@tanstack/react-router";
import { Suspense } from "react";

import { ProjectContentLoader } from "@/components/ProjectContentLoader";
import { ProjectShell } from "@/components/ProjectShell";
import { RequireSignIn } from "@/lib/session";
import { WorkspaceProvider } from "@/panes/Workspace";
import type { Project } from "@/types/projects";

import "./p.$projectId/studio-theme.css";

export const Route = createFileRoute("/p/$projectId")({
  loader: async ({ params }) => {
    const res = await fetch(`/api/projects/${params.projectId}`, {
      credentials: "include",
    });
    if (res.status === 401) throw redirect({ to: "/" });
    if (!res.ok) throw new Error("Project not found");
    return (await res.json()) as {
      indexUrl: string;
      canManage: boolean;
      email: string | null;
    };
  },
  component: ProjectLayout,
});

function ProjectLayout() {
  const { projectId } = Route.useParams();
  const { indexUrl, email } = Route.useLoaderData();
  const [project] = useDocument<Project>(indexUrl as AutomergeUrl, {
    suspense: true,
  });
  const matchRoute = useMatchRoute();
  const { branch, scope, appId } = useParams({ strict: false });
  const isJig = scope !== undefined && appId !== undefined;
  const isWorkshop =
    !!matchRoute({ to: "/p/$projectId/workshop" }) ||
    !!matchRoute({ to: "/p/$projectId/$branch/workbench/$view" });
  const branchTaskId = branch && branch !== "main" ? branch : undefined;

  return (
    <RequireSignIn>
      <WorkspaceProvider className="bear-studio flex h-screen w-screen overflow-hidden bg-background text-foreground">
        <ProjectShell
          key={projectId}
          projectId={projectId}
          indexUrl={indexUrl as AutomergeUrl}
          projectName={project.name}
          projectEmail={email}
          section={isJig ? "jig" : isWorkshop ? "workshop" : "explorer"}
          taskId={branchTaskId}
        >
          <Suspense fallback={<ProjectContentLoader />}>
            <Outlet />
          </Suspense>
        </ProjectShell>
      </WorkspaceProvider>
    </RequireSignIn>
  );
}
